import {
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import { FormDatePicker } from '@/components/form/FormDatePicker';
import FormInput from '@/components/form/FormInput';
import FormMultiSelect, { Option } from '@/components/form/FormMultiSelect';
import FormSwitch from '@/components/form/FormSwitch';
import FormTextEditor from '@/components/form/FormTextEditor';
import Card from '@/components/ui/Card/Card';
import { Header } from '@/components/ui/Header/Header';
import { Breadcrumbs } from '@/components/ui/Navigation/Breadcrumbs';
import { getDomainNames, useDomainsQuery } from '@/features/domains';
import { createRolesHook } from '@/hooks/useRoles';
import { Breadcrumb } from '@/types/Breadcrumbs';

import { useCreateSurveyMutation, useGetSurveyQuery, useUpdateSurveyMutation } from '../api';
import { SurveyFieldList } from '../components/SurveyFieldList';
import { SurveyFieldModal } from '../components/SurveyFieldModal';
import { CreateSurveyPayload, Survey, SurveyField, SurveyVisibilityScope } from '../types';
import { SURVEY_VISIBILITY_OPTIONS } from '../utils';

type SurveyFormValues = {
  title: string;
  description: string;
  isPublished: boolean;
  visibilityScope: SurveyVisibilityScope;
  startDate: Date | null;
  endDate: Date | null;
  schemas: Option[];
  fields: SurveyField[];
};

const surveySchema = yup.object({
  title: yup.string().trim().required('Tytuł ankiety jest wymagany'),
  description: yup.string().required('Opis ankiety jest wymagany'),
  isPublished: yup.boolean().default(false),
  visibilityScope: yup
    .mixed<SurveyVisibilityScope>()
    .oneOf(SURVEY_VISIBILITY_OPTIONS.map((option) => option.value))
    .required('Wybierz zakres widoczności'),
  startDate: yup.date().nullable(),
  endDate: yup
    .date()
    .nullable()
    .when('startDate', (startDate, schema) =>
      startDate
        ? schema.min(startDate, 'Data zakończenia nie może być wcześniejsza niż data startu')
        : schema
    ),
  schemas: yup.array().of(
    yup.object({
      value: yup.string().required(),
      label: yup.string().required(),
    })
  ),
  fields: yup.array().of(yup.mixed()).default([]),
});

const getDefaultValues = (): SurveyFormValues => ({
  title: '',
  description: '',
  isPublished: false,
  visibilityScope: 'all',
  startDate: null,
  endDate: null,
  schemas: [],
  fields: [],
});

const mapSurveyToFormValues = (survey: Survey): SurveyFormValues => ({
  title: survey.title,
  description: survey.description,
  isPublished: survey.isPublished,
  visibilityScope: survey.visibilityScope,
  startDate: survey.startDate ? new Date(survey.startDate) : null,
  endDate: survey.endDate ? new Date(survey.endDate) : null,
  schemas: survey.schemas
    ? survey.schemas.map((schema) => ({
        value: schema,
        label: schema,
      }))
    : [],
  fields: survey.fields
    ? survey.fields.map((field) => ({
        ...field,
        options: field.options ? field.options.map((option) => ({ ...option })) : undefined,
      }))
    : [],
});

const useRoles = createRolesHook(['user', 'admin']);

type SurveyFormPageProps = {
  mode: 'create' | 'edit';
  globalId?: string;
};

const SurveyFormPage = ({ mode, globalId }: SurveyFormPageProps) => {
  useRoles();
  const navigate = useNavigate();
  const { data: domains, isLoading: isLoadingDomains } = useDomainsQuery();
  const [createSurvey, { isLoading: isCreating }] = useCreateSurveyMutation();
  const [updateSurvey, { isLoading: isUpdating }] = useUpdateSurveyMutation();
  const {
    data: survey,
    isLoading: isSurveyLoading,
    isFetching: isSurveyFetching,
    isError: isSurveyError,
  } = useGetSurveyQuery(globalId ?? '', {
    skip: mode !== 'edit' || !globalId,
  });
  const defaultValues = useMemo(
    () => (mode === 'edit' && survey ? mapSurveyToFormValues(survey) : getDefaultValues()),
    [mode, survey]
  );
  const editRedirectRef = useRef(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<SurveyFormValues>({
    resolver: yupResolver(surveySchema),
    defaultValues,
    mode: 'onBlur',
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingField, setEditingField] = useState<SurveyField | null>(null);
  const [selectAllSchemas, setSelectAllSchemas] = useState(false);
  const fields = watch('fields') || [];
  const hasFields = fields.length > 0;

  const isMutating = mode === 'edit' ? isUpdating : isCreating;
  const canSubmit = hasFields && !isSubmitting && !isMutating && (mode === 'create' || !!globalId);

  const schemaOptions: Option[] = useMemo(
    () =>
      domains?.map((domain) => ({
        label: domain.name,
        value: domain.domain,
      })) ?? [],
    [domains]
  );

  useEffect(() => {
    if (mode === 'edit' && !globalId) {
      toast.error('Nie znaleziono ankiety do edycji.');
      navigate('/app/surveys');
    }
  }, [mode, globalId, navigate]);

  useEffect(() => {
    if (mode === 'edit' && isSurveyError) {
      toast.error('Nie udało się pobrać ankiety.');
      navigate('/app/surveys');
    }
  }, [mode, isSurveyError, navigate]);

  useEffect(() => {
    if (mode === 'edit' && survey) {
      reset(defaultValues);
      setSelectAllSchemas(false);
    }
  }, [mode, survey, defaultValues, reset]);

  useEffect(() => {
    if (mode === 'edit' && survey && schemaOptions.length > 0) {
      const mapped = (survey.schemas ?? []).map((schema) => {
        const existing = schemaOptions.find((option) => option.value === schema);
        return existing ?? { value: schema, label: schema };
      });
      setValue('schemas', mapped, { shouldDirty: false });
    }
  }, [mode, survey, schemaOptions, setValue]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty) {
        return;
      }
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  useEffect(() => {
    if (mode === 'edit' && survey?.isPublished && !editRedirectRef.current) {
      editRedirectRef.current = true;
      toast.info('Opublikowana ankieta nie może być edytowana.');
      navigate(`/app/surveys/${survey.globalId}`);
    }
  }, [mode, survey, navigate]);

  const handleCancel = () => {
    if (
      isDirty &&
      !window.confirm('Masz niezapisane zmiany. Czy na pewno chcesz wyjść z kreatora?')
    ) {
      return;
    }
    navigate('/app/surveys');
  };

  const handleManageField = (field?: SurveyField) => {
    setEditingField(field ?? null);
    onOpen();
  };

  const handleFieldSave = (field: SurveyField) => {
    const currentFields = getValues('fields') || [];
    const exists = currentFields.some((item) => item.id === field.id);
    const nextFields = exists
      ? currentFields.map((item) => (item.id === field.id ? field : item))
      : [...currentFields, field];

    setValue('fields', nextFields, { shouldDirty: true });
    setEditingField(null);
    onClose();
  };

  const handleFieldRemove = (fieldId: string) => {
    const currentFields = getValues('fields') || [];
    const nextFields = currentFields.filter((field) => field.id !== fieldId);
    setValue('fields', nextFields, { shouldDirty: true });
    if (editingField?.id === fieldId) {
      setEditingField(null);
    }
  };

  const onSubmit = async (values: SurveyFormValues) => {
    if (!values.fields || values.fields.length === 0) {
      toast.error('Dodaj przynajmniej jedno pole ankiety, aby zapisać.');
      return;
    }
    if (mode === 'edit' && !globalId) {
      toast.error('Brak identyfikatora ankiety do aktualizacji.');
      return;
    }

    const payload: CreateSurveyPayload = {
      title: values.title.trim(),
      description: values.description,
      isPublished: values.isPublished,
      visibilityScope: values.visibilityScope,
      startDate: values.startDate ? dayjs(values.startDate).toISOString() : null,
      endDate: values.endDate ? dayjs(values.endDate).toISOString() : null,
      schemas: values.schemas.length > 0 ? values.schemas.map((schema) => schema.value) : undefined,
      fields: values.fields.map((field) => ({
        id: field.id,
        label: field.label,
        isRequired: field.type === 'divider' ? false : field.isRequired,
        type: field.type,
        options:
          field.type === 'radio' || field.type === 'checkbox'
            ? field.options?.map((option) => ({
                id: option.id,
                label: option.label,
              }))
            : undefined,
        maxRate: field.type === 'scale' ? field.maxRate : undefined,
      })),
    };

    try {
      const { failedSchemas = [] } =
        mode === 'edit' && globalId
          ? await updateSurvey({ globalId, payload }).unwrap()
          : await createSurvey(payload).unwrap();
      if (failedSchemas.length > 0) {
        const failedNames = getDomainNames(failedSchemas, domains);
        toast.warning(
          `Ankieta nie dotarła do placówek: ${failedNames.join(
            ', '
          )}. Możesz ją opublikować ponownie w szczegółach ankiety.`
        );
      } else {
        toast.success(
          mode === 'edit' ? 'Ankieta została zaktualizowana.' : 'Ankieta została utworzona.'
        );
      }
      if (mode !== 'edit') {
        reset(getDefaultValues());
        setSelectAllSchemas(false);
      }
      navigate('/app/surveys');
    } catch (error: any) {
      const fallbackMessage =
        mode === 'edit'
          ? 'Nie udało się zaktualizować ankiety.'
          : 'Nie udało się utworzyć ankiety.';
      toast.error(error?.data?.message || fallbackMessage);
    }
  };

  const missingGlobalId = mode === 'edit' && !globalId;
  const breadcrumbs = useMemo<Breadcrumb[]>(() => {
    const items: Breadcrumb[] = [
      {
        path: '/app/surveys',
        displayValue: 'Ankiety',
        isCurrentRoute: false,
      },
    ];
    if (mode === 'edit') {
      items.push({
        path: globalId ? `/app/surveys/${globalId}/edit` : '/app/surveys',
        displayValue: 'Edycja ankiety',
        isCurrentRoute: true,
      });
    } else {
      items.push({
        path: '/app/surveys/new',
        displayValue: 'Nowa ankieta',
        isCurrentRoute: true,
      });
    }
    return items;
  }, [mode, globalId]);

  const headerTitle = mode === 'edit' ? 'Edytuj ankietę' : 'Nowa ankieta';

  if (mode === 'edit' && (isSurveyLoading || isSurveyFetching)) {
    return (
      <Center py={20}>
        <Spinner color="purple.500" />
      </Center>
    );
  }

  if (mode === 'edit' && isSurveyError) {
    return null;
  }

  if (missingGlobalId) {
    return null;
  }

  if (mode === 'edit' && survey?.isPublished) {
    return null;
  }

  return (
    <Stack spacing={6}>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <Stack spacing={1}>
        <Header pb={0}>{headerTitle}</Header>
      </Stack>

      <HStack justify="flex-end" spacing={3}>
        <Button onClick={handleCancel} variant="ghost">
          Anuluj
        </Button>
        <Button
          colorScheme="purple"
          onClick={handleSubmit(onSubmit)}
          isLoading={isSubmitting || isMutating}
          isDisabled={!canSubmit}
        >
          Zapisz ankietę
        </Button>
      </HStack>

      <Tabs colorScheme="purple" variant="enclosed">
        <TabList>
          <Tab
            _selected={{
              color: 'gray.800',
              bg: 'white',
              borderColor: 'purple.500',
              fontWeight: 'semibold',
            }}
          >
            Konfiguracja
          </Tab>
          <Tab
            _selected={{
              color: 'gray.800',
              bg: 'white',
              borderColor: 'purple.500',
              fontWeight: 'semibold',
            }}
          >
            Struktura ankiety
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} alignItems="start">
              <Card>
                <Stack spacing={4} color="gray.800">
                  <Heading size="sm">Podstawowe informacje</Heading>
                  <FormInput
                    label="Tytuł ankiety"
                    placeholder="Np. Badanie satysfakcji rodziców"
                    {...register('title')}
                    error={errors.title}
                  />
                  <FormTextEditor control={control} name="description" />
                  <FormSwitch control={control} name="isPublished" label="Opublikuj po zapisaniu" />
                </Stack>
              </Card>
              <Card>
                <Stack spacing={4} color="gray.800">
                  <Heading size="sm">Widoczność i harmonogram</Heading>
                  <FormControl isInvalid={!!errors.visibilityScope}>
                    <FormLabel>Zakres widoczności</FormLabel>
                    <Select placeholder="Wybierz zakres" {...register('visibilityScope')}>
                      {SURVEY_VISIBILITY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>Data rozpoczęcia</FormLabel>
                      <FormDatePicker
                        control={control}
                        name="startDate"
                        placeholderText="Wybierz datę"
                        isClearable
                      />
                    </FormControl>
                    <FormControl isInvalid={!!errors.endDate}>
                      <FormLabel>Data zakończenia</FormLabel>
                      <FormDatePicker
                        control={control}
                        name="endDate"
                        placeholderText="Wybierz datę"
                        isClearable
                      />
                    </FormControl>
                  </SimpleGrid>

                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                      Domeny publikacji
                    </Text>
                    <FormMultiSelect
                      control={control}
                      name="schemas"
                      label="Wybierz domeny"
                      options={schemaOptions}
                      emptyStateLabel="Brak dostępnych domen"
                      errors={errors}
                      disabled={isLoadingDomains || selectAllSchemas}
                      selectAll={selectAllSchemas}
                    />
                    <Checkbox
                      colorScheme="purple"
                      checked={selectAllSchemas}
                      onChange={(event) => setSelectAllSchemas(event.target.checked)}
                      isDisabled={isLoadingDomains}
                      mt={2}
                    >
                      Wszystkie placówki
                    </Checkbox>
                  </Box>
                </Stack>
              </Card>
            </SimpleGrid>
          </TabPanel>
          <TabPanel px={0}>
            <Stack spacing={6}>
              <Card>
                <Stack spacing={3} color="gray.800">
                  <HStack justify="space-between" align="center">
                    <Heading size="sm">Pytania i elementy</Heading>
                    <Button colorScheme="purple" onClick={() => handleManageField()}>
                      Dodaj pole
                    </Button>
                  </HStack>
                  <Divider />
                  <SurveyFieldList
                    fields={fields}
                    onEdit={handleManageField}
                    onRemove={handleFieldRemove}
                  />
                </Stack>
              </Card>
            </Stack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <SurveyFieldModal
        isOpen={isOpen}
        initialValue={editingField ?? undefined}
        onClose={() => {
          onClose();
          setEditingField(null);
        }}
        onSubmit={handleFieldSave}
      />
    </Stack>
  );
};

export const SurveyCreatePage = () => <SurveyFormPage mode="create" />;

export const SurveyEditPage = () => {
  const { globalId } = useParams<{ globalId: string }>();
  return <SurveyFormPage mode="edit" globalId={globalId} />;
};
